import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { supabase } from '../../../lib/supabase';
import { useToast } from '../../../components/ui/use-toast';
import { Loader2, Mail, Search, MessageSquare, Reply, X, Trash2, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { useCheckPermissions } from '../../../middleware/checkPermissions';
import { sendReplyEmail } from '../../../services/emailService';
import { useDomainQuery } from '../../../hooks/useDomainQuery';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../components/ui/dialog';

interface Message {
  id: number;
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  created_at: string;
  replied: boolean;
  reply_text?: string;
  reply_date?: string;
  read: boolean;
}

export const MessageList = () => {
  useCheckPermissions(['admin']);
  const { toast } = useToast();
  const { applyDomainFilter, getFilterParams } = useDomainQuery();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [viewMode, setViewMode] = useState<'all' | 'replied' | 'unreplied'>('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingMessage, setDeletingMessage] = useState(false);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      // Utiliser la logique domain -> tenant_id fallback
      let query = supabase
        .from('contact_messages')
        .select('*')
        .order('created_at', { ascending: false });
      
      // Appliquer le filtre domain/tenant_id
      query = applyDomainFilter(query);

      if (searchQuery) {
        query = query.or(`name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%,subject.ilike.%${searchQuery}%,message.ilike.%${searchQuery}%`);
      }

      if (viewMode === 'replied') {
        query = query.eq('replied', true);
      } else if (viewMode === 'unreplied') {
        query = query.eq('replied', false);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      setMessages(data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des messages:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de récupérer les messages',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, [searchQuery, viewMode]);

  const handleViewMessage = async (message: Message) => {
    setSelectedMessage(message);

    // Marquer le message comme lu s'il ne l'est pas déjà
    if (message.read === false) {
      try {
        const { error } = await supabase
          .from('contact_messages')
          .update({ read: true })
          .eq('id', message.id);

        if (error) throw error;

        // Mettre à jour le message dans l'état local
        setMessages(messages.map((m) =>
          m.id === message.id ? { ...m, read: true } : m
        ));
      } catch (error) {
        console.error('Erreur lors du marquage du message comme lu:', error);
      }
    }
  };

  const handleOpenReplyDialog = (message: Message) => {
    setSelectedMessage(message);
    setReplyDialogOpen(true);
    setReplyText('');
  };

  const handleSendReply = async () => {
    if (!selectedMessage || !replyText.trim()) return;

    setSendingReply(true);
    try {
      // Utiliser les paramètres de filtrage domain/tenant
      const { tenantId, domain } = getFilterParams();
      
      // Utiliser le service d'email pour envoyer la réponse
      const result = await sendReplyEmail({
        to: selectedMessage.email,
        name: selectedMessage.name,
        subject: selectedMessage.subject,
        replyText: replyText,
        messageId: selectedMessage.id,
        tenantId: tenantId,
        domain: domain
      });
      
      if (!result.success) {
        throw new Error(result.error);
      }

      toast({
        title: 'Réponse envoyée',
        description: `Votre réponse a été envoyée à ${selectedMessage.name}`,
        variant: 'default',
      });

      // Fermer le dialogue et rafraîchir les messages
      setReplyDialogOpen(false);
      fetchMessages();
    } catch (error: any) {
      console.error('Erreur lors de l\'envoi de la réponse:', error);
      toast({
        title: 'Erreur',
        description: error && error.message ? error.message : 'Impossible d\'envoyer la réponse',
        variant: 'destructive',
      });
    } finally {
      setSendingReply(false);
    }
  };

  const handleOpenDeleteDialog = (message: Message) => {
    setSelectedMessage(message);
    setDeleteDialogOpen(true);
  };

  const handleDeleteMessage = async () => {
    if (!selectedMessage) return;

    setDeletingMessage(true);
    try {
      const { error } = await supabase
        .from('contact_messages')
        .delete()
        .eq('id', selectedMessage.id);

      if (error) throw error;

      toast({
        title: 'Message supprimé',
        description: 'Le message a été supprimé avec succès',
        variant: 'default',
      });

      setDeleteDialogOpen(false);
      setSelectedMessage(null);
      fetchMessages();
    } catch (error) {
      console.error('Erreur lors de la suppression du message:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer le message',
        variant: 'destructive',
      });
    } finally {
      setDeletingMessage(false);
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'dd MMMM yyyy à HH:mm', { locale: fr });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">Messages de contact</h1>
            <p className="text-gray-400">Gérez les messages reçus via le formulaire de contact</p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className={viewMode === 'all' ? 'bg-yellow-400/20 border-yellow-400/30 text-yellow-400' : ''}
              onClick={() => setViewMode('all')}
            >
              Tous
            </Button>
            <Button
              variant="outline"
              size="sm"
              className={viewMode === 'unreplied' ? 'bg-yellow-400/20 border-yellow-400/30 text-yellow-400' : ''}
              onClick={() => setViewMode('unreplied')}
            >
              Non répondus
            </Button>
            <Button
              variant="outline"
              size="sm"
              className={viewMode === 'replied' ? 'bg-yellow-400/20 border-yellow-400/30 text-yellow-400' : ''}
              onClick={() => setViewMode('replied')}
            >
              Répondus
            </Button>
          </div>
        </div>

        {/* Barre de recherche */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-3 py-2.5 bg-white/5 border border-white/10 text-white rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-transparent"
              placeholder="Rechercher un message..."
            />
          </div>
        </div>

        {/* Liste des messages */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center p-12">
              <Loader2 className="h-8 w-8 text-yellow-400 animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <div className="p-12 text-center">
              <MessageSquare className="h-12 w-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">Aucun message</h3>
              <p className="text-gray-400">
                {searchQuery
                  ? 'Aucun message ne correspond à votre recherche'
                  : viewMode === 'unreplied'
                    ? 'Tous les messages ont été répondus'
                    : viewMode === 'replied'
                      ? 'Aucun message n\'a encore été répondu'
                      : 'Vous n\'avez reçu aucun message pour le moment'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Expéditeur
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Sujet
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {messages.map((message, index) => (
                    <tr key={message.id} className={`hover:bg-white/10 ${index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-yellow-400/10 rounded-full flex items-center justify-center">
                            <Mail className="h-5 w-5 text-yellow-400" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white">{message.name}</div>
                            <div className="text-sm text-gray-400">{message.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white line-clamp-1">{message.subject}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {formatDate(message.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {message.replied ? (
                          <Badge className="bg-green-400/20 text-green-400 border-green-400/20">
                            Répondu
                          </Badge>
                        ) : (
                          <Badge className="bg-yellow-400/20 text-yellow-400 border-yellow-400/20">
                            En attente
                          </Badge>
                        )}
                        {message.read ? (
                          <Badge className="bg-blue-400/20 text-blue-400 border-blue-400/20 ml-2">
                            Lu
                          </Badge>
                        ) : (
                          <Badge className="bg-red-400/20 text-red-400 border-red-400/20 ml-2">
                            Non lu
                          </Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewMessage(message)}
                            className="text-gray-300 hover:text-white"
                          >
                            Voir
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenReplyDialog(message)}
                            className="text-yellow-400 hover:text-yellow-300"
                          >
                            Répondre
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDeleteDialog(message)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Dialogue de visualisation du message */}
      {selectedMessage && (
        <Dialog open={!!selectedMessage && !replyDialogOpen} onOpenChange={() => setSelectedMessage(null)}>
          <DialogContent className="sm:max-w-2xl bg-black/95 border border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-white">
                {selectedMessage.subject}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6 mt-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-gray-300">De: <span className="text-white">{selectedMessage.name}</span></p>
                  <p className="text-sm text-gray-300">Email: <span className="text-white">{selectedMessage.email}</span></p>
                </div>
                <p className="text-sm text-gray-300">
                  {formatDate(selectedMessage.created_at)}
                </p>
              </div>

              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <p className="text-white whitespace-pre-wrap">{selectedMessage.message}</p>
              </div>

              {selectedMessage.replied && selectedMessage.reply_text && (
                <div>
                  <h3 className="text-md font-medium text-white mb-2 flex items-center gap-2">
                    <Reply className="h-4 w-4 text-green-400" />
                    Votre réponse
                    {selectedMessage.reply_date && (
                      <span className="text-sm font-normal text-gray-400 ml-2">
                        ({formatDate(selectedMessage.reply_date)})
                      </span>
                    )}
                  </h3>
                  <div className="bg-green-400/10 rounded-xl p-4 border border-green-400/20">
                    <p className="text-white whitespace-pre-wrap">{selectedMessage.reply_text}</p>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="mt-6">
              <Button
                variant="outline"
                onClick={() => setSelectedMessage(null)}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Fermer
              </Button>
              {!selectedMessage.replied && (
                <Button
                  onClick={() => {
                    setReplyDialogOpen(true);
                    setReplyText('');
                  }}
                  className="bg-yellow-400 text-black hover:bg-yellow-500"
                >
                  <Reply className="h-4 w-4 mr-2" />
                  Répondre
                </Button>
              )}
              <Button
                variant="destructive"
                onClick={() => {
                  setSelectedMessage(null);
                  handleOpenDeleteDialog(selectedMessage);
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Supprimer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialogue de réponse */}
      <Dialog open={replyDialogOpen} onOpenChange={setReplyDialogOpen}>
        <DialogContent className="sm:max-w-2xl bg-black/95 border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">
              Répondre à {selectedMessage?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <h3 className="text-md font-medium text-white mb-2">Message original:</h3>
              <p className="text-gray-300 whitespace-pre-wrap">{selectedMessage?.message}</p>
{selectedMessage?.phone && (
  <p className="text-sm text-gray-300 mt-2">
    <span className="font-medium text-white">Téléphone :</span> {selectedMessage.phone}
  </p>
) }
            </div>

            <div>
              <label htmlFor="reply" className="block text-sm font-medium text-gray-300 mb-2">
                Votre réponse
              </label>
              <Textarea
                id="reply"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                rows={6}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/50"
                placeholder="Saisissez votre réponse..."
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setReplyDialogOpen(false)}
              disabled={sendingReply}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <X className="h-4 w-4 mr-2" />
              Annuler
            </Button>
            <Button
              onClick={handleSendReply}
              disabled={!replyText.trim() || sendingReply}
              className="bg-yellow-400 text-black hover:bg-yellow-500"
            >
              {sendingReply ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Reply className="h-4 w-4 mr-2" />
                  Envoyer la réponse
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialogue de confirmation de suppression */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md bg-black/95 border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2 text-white">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              Confirmer la suppression
            </DialogTitle>
            <DialogDescription className="text-gray-300">
              Êtes-vous sûr de vouloir supprimer ce message ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>

          {selectedMessage && (
            <div className="mt-4 bg-white/5 rounded-xl p-4 border border-white/10">
              <p className="text-sm text-gray-300 mb-1">
                <span className="font-medium text-white">De :</span> {selectedMessage.name}
              </p>
              <p className="text-sm text-gray-300 mb-1">
                <span className="font-medium text-white">Sujet :</span> {selectedMessage.subject}
              </p>
              <p className="text-sm text-gray-300">
                <span className="font-medium text-white">Date :</span> {formatDate(selectedMessage.created_at)}
              </p>
            </div>
          )}

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deletingMessage}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteMessage}
              disabled={deletingMessage}
            >
              {deletingMessage ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Supprimer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default MessageList;
